import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './DealPage.css';
import { BENEFICIOS, PREMIOS, aperturasPara, autoAperturas, aperturasDisponibles } from './ruleta';
import Rueda from './Rueda';
import { Icono } from './iconos';
import EfectoPremio from './EfectoPremio';
import Maletin from './Maletin';
import {
    FORMATIONS, slotsDeFormacion, TOTAL_RONDAS,
    valorDeOferta, elegirJugadorOferta, humorBanquero, slotsPosiblesPara,
    mezclar, fuerzaEquipo, layoutFormacion, calorDeApertura,
} from './dealEngine';
import { pickFour12Character } from '../../components/BotCharacter/four12Characters';
// Las fotos van SIEMPRE por este helper: traduce las URLs de SofaScore y Cloudinary al CDN
// propio. Usando player.img crudo, todos los jugadores con esas URLs quedaban sin foto —que es
// como estaba y por eso faltaban tantas caras.
import { getImgSrc } from '../../helpers/getImageSrc';
import defaultPlayerImg from '../../img/defaultPlayer.jpg';
import Copa from './Copa';

const backendURL = process.env.REACT_APP_BACKEND_URL || 'https://footy-tictactoe-production.up.railway.app';

const fmtM = (v) => v < 1e6
    ? `€${Math.round(v / 1e3)}k`
    : `€${(v / 1e6).toFixed(v < 1e7 ? 1 : 0)}M`;

export default function DealPage() {
    const navigate = useNavigate();

    const [formacion, setFormacion] = useState(null);
    const [cantMaletines, setCantMaletines] = useState(10);
    const [cantOfertas, setCantOfertas] = useState(3);
    const [beneficio, setBeneficio] = useState(null);        // ruleta inicial, para toda la partida
    const [efectos, setEfectos] = useState({});              // efectos pendientes para la ronda que viene
    const [ruleta, setRuleta] = useState(null);              // { tipo:'inicial'|'premio', girando, resultado }
    const [plantel, setPlantel] = useState({});        // indice de slot -> jugador
    const [ronda, setRonda] = useState(0);
    const [banquero] = useState(() => pickFour12Character(null));

    const [maletines, setMaletines] = useState([]);
    const [poolOfertas, setPoolOfertas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);

    const [miMaletin, setMiMaletin] = useState(null);
    const [tanda, setTanda] = useState(0);
    const aperturas = useMemo(() => aperturasPara(cantMaletines, cantOfertas), [cantMaletines, cantOfertas]);
    const [porAbrir, setPorAbrir] = useState(4);
    const [oferta, setOferta] = useState(null);
    const [ofrecidos, setOfrecidos] = useState([]);
    const [porUbicar, setPorUbicar] = useState(null);   // jugador ganado, esperando slot
    const [cambioFinal, setCambioFinal] = useState(null); // { mio, rival } — el clasico cambiazo
    const [destello, setDestello] = useState(null);       // { numero, calor } — rojo/verde al abrir
    const [ultimoPuesto, setUltimoPuesto] = useState(null);
    const [revelacion, setRevelacion] = useState(null);   // { elegido, descartado, eraElMio }
    const [torneo, setTorneo] = useState(null);

    const hayRuleta = !!ruleta;
    const slots = useMemo(() => (formacion ? slotsDeFormacion(formacion) : []), [formacion]);
    const slotsLibres = useMemo(
        () => slots.map((s, i) => ({ s, i })).filter(({ i }) => !plantel[i]),
        [slots, plantel]
    );
    const yaFichados = useMemo(() => Object.values(plantel).map(j => j._id), [plantel]);

    // ── Traer la ronda ──────────────────────────────────────────────────────
    const cargarRonda = useCallback(async () => {
        if (!formacion) return;
        // Sin puestos libres no hay ronda que pedir: el plantel ya esta completo.
        if (slotsLibres.length === 0) { setTorneo(true); return; }
        setCargando(true); setError(null);
        setMiMaletin(null); setTanda(0); setPorAbrir(aperturas[0]);
        setOferta(null); setOfrecidos([]); setCambioFinal(null); setRevelacion(null);
        try {
            const libres = [...new Set(slotsLibres.map(x => x.s))].join(',');
            const params = new URLSearchParams({ slots: libres, excluir: yaFichados.join(','), cantidad: cantMaletines });
            if (efectos.minM) params.set('minM', efectos.minM);
            if (efectos.maxM) params.set('maxM', efectos.maxM);
            const url = `${backendURL}/deal/round?${params}`;
            const r = await fetch(url);
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || `Error ${r.status}`);
            const mezclados = mezclar(d.maletines).map((m, i) => ({ ...m, numero: i + 1, abierto: false }));
            setMaletines(mezclados);
            setPoolOfertas(d.ofertas || []);
            // "Manos apuradas": se abren solos los mas baratos, y el jugador arranca la ronda con
            // menos informacion en juego.
            if (efectos.autoAbrir) {
                const baratos = [...mezclados].sort((a, b) => a.valor - b.valor)
                    .slice(0, efectos.autoAbrir).map(m => m.numero);
                setMaletines(mezclados.map(m => baratos.includes(m.numero) ? { ...m, abierto: true, calor: 'barato' } : m));
                // Todavia no hay maletin propio elegido, asi que de los ya abiertos uno va a ser
                // rival y el resto tambien: la primera tanda se acota a lo que quede.
                setPorAbrir(aperturasDisponibles(aperturas[0], mezclados.length - baratos.length - 1));
            }
            setEfectos({});   // los efectos duran una sola ronda
        } catch (e) {
            setError(e.message);
        } finally {
            setCargando(false);
        }
    }, [formacion, slotsLibres, yaFichados, cantMaletines, efectos, aperturas]);

    useEffect(() => {
        // Solo al empezar una ronda nueva: no cuando cambia el plantel a mitad de mano.
        if (formacion && !porUbicar && !torneo && !hayRuleta) cargarRonda();
        // `hayRuleta` es un booleano y no el objeto: durante el giro el objeto cambia varias veces
        // y volveria a pedir la ronda a mitad de la animacion. Como booleano solo dispara cuando
        // la ruleta se cierra, que es justo cuando corresponde cargar la mano siguiente.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formacion, ronda, hayRuleta]);

    const valoresEnJuego = useMemo(() => maletines.filter(m => !m.abierto).map(m => m.valor), [maletines]);
    const elMio = maletines.find(m => m.numero === miMaletin) || null;

    // ── Abrir maletines ─────────────────────────────────────────────────────
    const abrir = (numero) => {
        if (oferta || porUbicar) return;
        if (miMaletin === null) { setMiMaletin(numero); return; }
        if (numero === miMaletin) return;
        const m = maletines.find(x => x.numero === numero);
        if (!m || m.abierto) return;

        // Rojo si se fue uno de los caros, verde si era de los baratos. Se mide contra la mediana
        // de lo que seguia en juego ANTES de abrirlo.
        const antes = maletines.filter(x => !x.abierto).map(x => x.valor);
        const calor = calorDeApertura(m.valor, antes);
        const nuevos = maletines.map(x => x.numero === numero ? { ...x, abierto: true, calor } : x);
        setMaletines(nuevos);
        if (calor) {
            setDestello({ numero, calor });
            setTimeout(() => setDestello(d => (d && d.numero === numero ? null : d)), 900);
        }
        const quedan = porAbrir - 1;
        setPorAbrir(quedan);

        // Si ya no quedan rivales para abrir, se corta la tanda aunque falten aperturas.
        const rivalesLibres = nuevos.filter(x => !x.abierto && x.numero !== miMaletin).length;
        if (quedan === 0 || rivalesLibres === 0) {
            const restantes = nuevos.filter(x => !x.abierto).map(x => x.valor);
            const objetivo = valorDeOferta(restantes, tanda);
            const jugador = elegirJugadorOferta(poolOfertas, objetivo, ofrecidos);
            if (jugador) setOferta({ jugador, objetivo });
            else ganar(elMio);   // sin pool no hay oferta posible: te quedás con el tuyo
        }
    };

    // ── Resolver la mano ────────────────────────────────────────────────────
    const ganar = (jugador) => {
        // La oferta se cierra SIEMPRE acá. Antes solo se limpiaba dentro de ubicar(), asi que
        // cuando el jugador entraba en mas de un puesto quedaban los dos paneles a la vez —el de
        // la oferta y el de "donde lo pongo"— y parecia que pedia aceptar el trato de nuevo.
        setOferta(null);
        const posibles = slotsPosiblesPara(jugador, slotsLibres.map(x => x.s));
        const candidatos = slotsLibres.filter(x => posibles.includes(x.s));
        if (candidatos.length === 1) ubicar(jugador, candidatos[0].i);
        else setPorUbicar({ jugador, candidatos });
    };

    const ubicar = (jugador, indiceSlot) => {
        const completo = { ...plantel, [indiceSlot]: jugador };
        setPlantel(completo);
        setUltimoPuesto(indiceSlot);
        setPorUbicar(null);
        setOferta(null);
        // El juego termina cuando no quedan puestos libres, no a las 11 rondas: la calavera puede
        // liberar un puesto y entonces hace falta una ronda mas para volver a llenarlo.
        if (Object.keys(completo).length >= slots.length) { setRonda(r => r + 1); setTorneo(true); return; }

        // La ruleta sale despues de sumar un jugador, nunca despues del ultimo. La ronda NO avanza
        // todavia: los efectos que cambian la mano siguiente (mercado caro, manos apuradas) tienen
        // que estar seteados antes de que se pida esa ronda.
        setRuleta({ tipo: 'premio', girando: false, resultado: null, pendiente: true });
    };

    /**
     * Aceptar el trato revela primero tu maletin: sin eso te quedabas con el jugador ofrecido
     * pero nunca sabias que habias dejado pasar, que es la mitad de la gracia de aceptar.
     */
    /**
     * Aplica lo que salio en la ruleta. Los efectos que tocan el plantel se resuelven contra el
     * backend porque necesitan buscar un jugador real; los que afectan la ronda siguiente quedan
     * guardados en `efectos` y los consume cargarRonda().
     */
    const aplicarPremio = async (premio) => {
        const idsPlantel = Object.values(plantel).map(j => j._id);
        try {
            if (premio.id === 'sobre') {
                const libres = [...new Set(slotsLibres.map(x => x.s))].join(',');
                if (!libres) return;
                const r = await fetch(`${backendURL}/deal/mejores?slots=${libres}&excluir=${idsPlantel.join(',')}`);
                const d = await r.json();
                const elegido = (d.jugadores || [])[Math.floor(Math.random() * (d.jugadores || []).length)];
                if (elegido) {
                    // El sobre entrega el jugador directo al plantel, sin pasar por la ronda.
                    const posibles = slotsPosiblesPara(elegido, slotsLibres.map(x => x.s));
                    const destino = slotsLibres.find(x => posibles.includes(x.s));
                    if (destino) {
                        setPlantel(prev => ({ ...prev, [destino.i]: elegido }));
                        setUltimoPuesto(destino.i);
                    }
                    return { tipo: 'sobre', jugador: elegido };
                }
            }

            if (premio.id === 'calavera') {
                const entradas = Object.entries(plantel);
                if (!entradas.length) return;
                const [idx, victima] = entradas.reduce((a, b) => (b[1].valor > a[1].valor ? b : a));
                setPlantel(prev => { const c = { ...prev }; delete c[idx]; return c; });
                return { tipo: 'calavera', jugador: victima };
            }

            if (premio.id === 'switch_bueno' || premio.id === 'switch_malo') {
                const entradas = Object.entries(plantel);
                if (!entradas.length) return;
                // El cambio a favor agarra al MAS BARATO del plantel: mejorar a uno que ya es caro
                // casi no mueve la aguja, y el premio se sentia desperdiciado. El cambio en contra
                // sigue siendo al azar, para que el castigo pueda doler de verdad.
                const esAFavor = premio.id === 'switch_bueno';
                const [idx, viejo] = esAFavor
                    ? entradas.reduce((a, b) => (b[1].valor < a[1].valor ? b : a))
                    : entradas[Math.floor(Math.random() * entradas.length)];
                const direccion = esAFavor ? 'arriba' : 'abajo';
                const r = await fetch(`${backendURL}/deal/cambio?slots=${slots[idx]}&valor=${viejo.valor / 1e6}&direccion=${direccion}&excluir=${idsPlantel.join(',')}`);
                const d = await r.json();
                if (d.jugador) {
                    setPlantel(prev => ({ ...prev, [idx]: d.jugador }));
                    return { tipo: premio.id, viejo, nuevo: d.jugador };
                }
            }

            if (premio.id === 'apuesta') {
                const libres = [...new Set(slotsLibres.map(x => x.s))].join(',');
                if (!libres) return;
                const r = await fetch(`${backendURL}/deal/sorteo?slots=${libres}&cuantos=5&minM=10&excluir=${idsPlantel.join(',')}`);
                const d = await r.json();
                // No se aplica nada todavia: la apuesta la juega el usuario y resuelve al final.
                if (d.jugadores?.length >= 2) return { tipo: 'apuesta', cartas: d.jugadores };
                return { tipo: 'error', mensaje: d.message || 'No hay cartas suficientes' };
            }

            if (premio.id === 'autoabrir') { setEfectos({ autoAbrir: autoAperturas(cantMaletines) }); return { tipo: 'autoabrir' }; }
            if (premio.id === 'suben')     { setEfectos({ minM: 30 }); return { tipo: 'suben' }; }
            if (premio.id === 'bajan')     { setEfectos({ maxM: 40 }); return { tipo: 'bajan' }; }
        } catch (e) {
            return { tipo: 'error', mensaje: e.message };
        }
    };

    /**
     * El premio se sortea ANTES de girar y la rueda se lleva hasta ese gajo: asi el efecto
     * aterriza donde corresponde y no depende de donde frene una animacion.
     */
    /** Ubica en el plantel al jugador que salio de la apuesta. */
    const cobrarApuesta = (jugador) => {
        const posibles = slotsPosiblesPara(jugador, slotsLibres.map(x => x.s));
        const destino = slotsLibres.find(x => posibles.includes(x.s));
        if (destino) {
            setPlantel(prev => ({ ...prev, [destino.i]: jugador }));
            setUltimoPuesto(destino.i);
        }
    };

    /**
     * Cierra la ruleta y decide que sigue.
     *
     * El sobre dorado y la apuesta suman jugadores FUERA del flujo de ronda, asi que el plantel
     * puede completarse antes de las 11 rondas. Sin este chequeo el juego avanzaba de ronda igual
     * y pedia maletines sin ningun puesto libre, con lo cual el backend respondia "Faltan las
     * posiciones libres (slots)".
     */
    const cerrarRuleta = () => {
        setRuleta(null);
        if (slotsLibres.length === 0) setTorneo(true);
        else setRonda(r => r + 1);
    };

    const girarRuleta = () => {
        const idx = Math.floor(Math.random() * PREMIOS.length);
        setRuleta(r => ({ ...r, girando: true, pendiente: false, ganadorIdx: idx }));
    };

    /** La rueda avisa cuando freno: recien ahi se aplica el efecto. */
    const alFrenarRuleta = async () => {
        const premio = PREMIOS[ruleta.ganadorIdx];
        const detalle = await aplicarPremio(premio);
        setRuleta(r => ({ ...r, girando: false, resultado: premio, detalle }));
    };

    const aceptarOferta = () => {
        setRevelacion({ mio: elMio, rival: oferta.jugador, elegidoNumero: null, eraTrato: true });
        setOferta(null);
    };

    // Al elegir se abren LOS DOS maletines: se ve que habia en cada uno y con cual te quedaste.
    // Sin mostrar el descartado no hay forma de saber si acertaste, que es la mitad de la gracia.
    // Se guardan mio y rival en su lugar, mas cual elegiste: la revelacion tiene que abrir cada
    // maletin DONDE ESTABA. Antes se mostraba el elegido siempre primero, asi que al quedarte con
    // el segundo parecia que el jugador salia del primero.
    const resolverCambio = (jugador) => {
        const { mio, rival } = cambioFinal;
        setRevelacion({ mio, rival, elegidoNumero: jugador.numero, eraElMio: jugador.numero === mio.numero });
        setCambioFinal(null);
    };

    const cerrarRevelacion = () => {
        const { mio, rival, elegidoNumero, eraTrato } = revelacion;
        // En un trato te llevas al que ofrecio el Banquero; en el duelo, el maletin que elegiste.
        const j = eraTrato ? rival : (elegidoNumero === mio.numero ? mio : rival);
        setRevelacion(null);
        ganar(j);
    };

    const rechazarOferta = () => {
        const siguienteTanda = tanda + 1;
        setOfrecidos(prev => [...prev, oferta.jugador]);
        setOferta(null);
        const rivalesCerrados = maletines.filter(m => !m.abierto && m.numero !== miMaletin);
        if (siguienteTanda >= aperturas.length || rivalesCerrados.length === 0) {
            // El cambiazo del final: si sobrevivio un rival, se puede cambiar el maletin propio
            // por ese. Es el momento del programa y sin esto la ultima decision no existia.
            if (rivalesCerrados.length === 1) setCambioFinal({ mio: elMio, rival: rivalesCerrados[0] });
            else ganar(elMio);
            return;
        }
        // Si la tanda siguiente no puede abrir nada —porque "Manos apuradas" ya se llevo los
        // maletines que sobraban— se va derecho al duelo final en vez de pedir abrir cero.
        const disponibles = aperturasDisponibles(aperturas[siguienteTanda], rivalesCerrados.length);
        if (disponibles === 0) {
            if (rivalesCerrados.length === 1) setCambioFinal({ mio: elMio, rival: rivalesCerrados[0] });
            else ganar(elMio);
            return;
        }
        setTanda(siguienteTanda);
        setPorAbrir(disponibles);
    };

    // ── Vistas ──────────────────────────────────────────────────────────────
    if (!formacion) {
        return (
            <div className="deal-wrap">
                <Header navigate={navigate} />

                <section className="deal-intro">
                    <h2>Armá un equipo a ciegas y ganá la copa</h2>
                    <p className="deal-intro-bajada">
                        Una ronda por puesto. En cada una hay maletines con un jugador adentro:
                        elegís uno sin saber quién es y vas abriendo el resto.
                    </p>

                    <ol className="deal-pasos">
                        <li><span className="deal-paso-num">1</span><strong>Elegís un maletín</strong>
                            <small>Queda tuyo, tapado, hasta el final de la ronda.</small></li>
                        <li><span className="deal-paso-num">2</span><strong>Abrís los demás</strong>
                            <small>Cada uno tacha su valor de la escalera. Si se va uno caro, peor para vos.</small></li>
                        <li><span className="deal-paso-num">3</span><strong>El Banquero ofrece</strong>
                            <small>Te cambia tu tapado por un jugador a la vista. ¿Trato o no trato?</small></li>
                        <li><span className="deal-paso-num">4</span><strong>Jugás la copa</strong>
                            <small>Con los once que te tocaron, cuadro de 32 equipos hasta la final.</small></li>
                    </ol>

                    <p className="deal-intro-nota">
                        Solo aparecen jugadores que puedan cubrir un puesto que tengas libre: en la
                        última ronda, si te quedó el arco, salen únicamente arqueros.
                    </p>
                </section>

                <div className="deal-opciones">
                    <div className="deal-opcion">
                        <span className="deal-opcion-tit">Maletines por ronda</span>
                        <div className="deal-opcion-botones">
                            {[8, 10].map(n => (
                                <button key={n}
                                    className={`deal-op${cantMaletines === n ? ' activo' : ''}`}
                                    onClick={() => {
                                        setCantMaletines(n);
                                        // Con 8 solo hay 7 rivales: repartir 6 aperturas en 3 tandas
                                        // deja casi nada entre oferta y oferta.
                                        if (n === 8) setCantOfertas(2);
                                    }}>{n}</button>
                            ))}
                        </div>
                    </div>
                    <div className="deal-opcion">
                        <span className="deal-opcion-tit">Ofertas del Banquero</span>
                        <div className="deal-opcion-botones">
                            {[2, 3].map(n => (
                                <button key={n}
                                    className={`deal-op${cantOfertas === n ? ' activo' : ''}`}
                                    disabled={cantMaletines === 8 && n === 3}
                                    title={cantMaletines === 8 && n === 3 ? 'Con 8 maletines no entran 3 ofertas' : undefined}
                                    onClick={() => setCantOfertas(n)}>{n}</button>
                            ))}
                        </div>
                        {cantMaletines === 8 && <small className="deal-opcion-nota">Con 8 maletines van 2 ofertas</small>}
                    </div>
                </div>

                <h3 className="deal-elegi">Elegí el esquema</h3>
                <div className="deal-formaciones">
                    {Object.keys(FORMATIONS).map(f => {
                        const slotsF = slotsDeFormacion(f);
                        const cuenta = slotsF.reduce((acc, x) => ({ ...acc, [x]: (acc[x] || 0) + 1 }), {});
                        return (
                            <button key={f} className="deal-formacion" onClick={() => { setFormacion(f); setRuleta({ tipo: 'inicial', girando: false, resultado: null, pendiente: true }); }}>
                                <span className="deal-formacion-nombre">{f}</span>
                                <span className="deal-formacion-mini">
                                    {['ataque', 'medio', 'defensa', 'arco'].map(linea => {
                                        const grupos = {
                                            ataque: ['LW', 'ST', 'RW'],
                                            medio: ['CAM', 'LM', 'CM', 'RM', 'CDM'],
                                            defensa: ['LB', 'CB', 'RB'],
                                            arco: ['GK'],
                                        }[linea];
                                        const n = grupos.reduce((t, g) => t + (cuenta[g] || 0), 0);
                                        return <span key={linea} className="deal-formacion-linea">{Array.from({ length: n }).map((_, i) => <i key={i} />)}</span>;
                                    })}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Ruleta inicial: el beneficio que acompaña toda la partida.
    if (ruleta?.tipo === 'inicial' && !beneficio) {
        return (
            <div className="deal-wrap">
                <Header navigate={navigate} />
                <RuletaInicial
                    onElegir={(b) => { setBeneficio(b); setRuleta(null); }}
                />
            </div>
        );
    }

    if (torneo) {
        return (
            <div className="deal-wrap">
                <Header navigate={navigate} />
                <div className="deal-columnas">
                    <div className="deal-izquierda">
                        <Copa plantel={plantel} slots={slots} formacion={formacion}
                            onReiniciar={() => window.location.reload()} />
                    </div>
                    {/* La cancha se oculta sola cuando el partido abre su modal (ver Copa.css) */}
                    <aside className="deal-derecha">
                        <Cancha slots={slots} plantel={plantel} formacion={formacion} resaltar={null} />
                    </aside>
                </div>
            </div>
        );
    }

    const humor = oferta ? humorBanquero(oferta.jugador.valor, valoresEnJuego) : 'normal';
    const pool = banquero?.phrases?.[humor] || banquero?.phrases?.normal || [];
    const frase = pool.length ? pool[ronda % pool.length] : '';

    return (
        <div className="deal-wrap">
            <Header navigate={navigate} />

            <div className="deal-progreso">
                <span>Ronda <strong>{ronda + 1}</strong>/{TOTAL_RONDAS}</span>
                <span>Puestos libres: {[...new Set(slotsLibres.map(x => x.s))].join(' · ') || '—'}</span>
            </div>

            <div className="deal-columnas">
            <div className="deal-izquierda">

            {cargando && <p className="deal-cargando">Preparando los maletines…</p>}
            {error && <p className="deal-error">{error} <button onClick={cargarRonda}>Reintentar</button></p>}

            {!cargando && !error && !porUbicar && !cambioFinal && !revelacion && (
                <>
                    <p className="deal-instruccion">
                        {miMaletin === null
                            ? `Elegí un maletín a ciegas. Puestos libres: ${[...new Set(slotsLibres.map(x => x.s))].join(', ')}`
                            : oferta ? 'Decidí.' : `Abrí ${porAbrir} maletín${porAbrir > 1 ? 'es' : ''}.`}
                    </p>
                    <div className="deal-cuerpo">
                    <ol className="deal-escalera">
                        {[...maletines].sort((a, b) => a.valor - b.valor).map(m => (
                            <li key={m.numero} className={m.abierto ? 'fuera' : ''}>{fmtM(m.valor)}</li>
                        ))}
                    </ol>
                    <div className="deal-tablero">
                        {maletines.map(m => (
                            <button key={m.numero}
                                className={[
                                    'deal-maletin',
                                    m.abierto ? 'abierto' : '',
                                    m.numero === miMaletin ? 'mio' : '',
                                    m.calor ? `calor-${m.calor}` : '',
                                    destello?.numero === m.numero ? `destello-${destello.calor}` : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => abrir(m.numero)}
                                disabled={m.abierto || !!oferta}>
                                {m.abierto ? (
                                    <>
                                        <img src={getImgSrc(m.img) || defaultPlayerImg} alt={m.nombre}
                                            onError={e => { e.target.src = defaultPlayerImg; }} />
                                        <span className="deal-nombre">{m.nombre}</span>
                                        <span className="deal-valor">{fmtM(m.valor)}</span>
                                    </>
                                ) : (
                                    <Maletin numero={m.numero}
                                        estado={m.numero === miMaletin ? 'mio' : 'cerrado'}
                                        pista={beneficio ? pistaDe(m, beneficio) : null} />
                                )}
                            </button>
                        ))}
                    </div>
                    </div>
                </>
            )}

            {oferta && (
                <section className="deal-oferta">
                    <div className="deal-banquero">
                        {banquero?.img && <img src={banquero.img} alt={banquero.nombre} />}
                        <div>
                            <strong>{banquero?.nombre || 'El Banquero'}</strong>
                            {frase && <p className="deal-frase">“{frase}”</p>}
                        </div>
                    </div>
                    <p className="deal-monto">Te cambio tu maletín por…</p>
                    <div className="deal-jugador-oferta">
                        <img src={getImgSrc(oferta.jugador.img) || defaultPlayerImg} alt={oferta.jugador.nombre}
                            onError={e => { e.target.src = defaultPlayerImg; }} />
                        <div>
                            <strong>{oferta.jugador.nombre}</strong>
                            <span>{oferta.jugador.posiciones.join(', ')} · {fmtM(oferta.jugador.valor)}</span>
                        </div>
                    </div>
                    <div className="deal-botones">
                        <button className="deal-si" onClick={aceptarOferta}>Trato</button>
                        <button className="deal-no" onClick={rechazarOferta}>No trato</button>
                    </div>
                </section>
            )}

            {cambioFinal && (
                <section className="deal-cambio">
                    <p>Quedan dos. ¿Te quedás con el tuyo o cambiás?</p>
                    <div className="deal-duelo">
                        <button className="deal-maletin deal-maletin--duelo mio" onClick={() => resolverCambio(cambioFinal.mio)}>
                            <Maletin numero={cambioFinal.mio.numero} estado="mio" />
                        </button>
                        <span className="deal-duelo-vs">VS</span>
                        <button className="deal-maletin deal-maletin--duelo" onClick={() => resolverCambio(cambioFinal.rival)}>
                            <Maletin numero={cambioFinal.rival.numero} estado="cerrado" />
                        </button>
                    </div>
                    <div className="deal-duelo-pie">
                        <small>el tuyo, desde el principio</small>
                        <small>el que sobrevivió</small>
                    </div>
                </section>
            )}

            {revelacion && (
                <section className="deal-cambio">
                    <p>{revelacion.eraTrato
                        ? 'Trato cerrado. Esto es lo que tenías adentro:'
                        : (revelacion.eraElMio ? 'Te quedaste con el tuyo.' : 'Cambiaste.')}</p>
                    <div className="deal-duelo">
                        <RevelaMaletin j={revelacion.mio}
                            elegido={revelacion.eraTrato ? false : revelacion.eraElMio}
                            mio etiqueta={revelacion.eraTrato ? 'lo dejaste ir' : undefined} />
                        <span className="deal-duelo-vs">VS</span>
                        <RevelaMaletin j={revelacion.rival}
                            elegido={revelacion.eraTrato ? true : !revelacion.eraElMio}
                            etiqueta={revelacion.eraTrato ? 'te lo llevaste' : undefined} />
                    </div>
                    {(() => {
                        const tuyo = revelacion.eraTrato ? revelacion.rival : (revelacion.eraElMio ? revelacion.mio : revelacion.rival);
                        const otro = revelacion.eraTrato ? revelacion.mio : (revelacion.eraElMio ? revelacion.rival : revelacion.mio);
                        const dif = tuyo.valor - otro.valor;
                        return (
                            <p className={dif >= 0 ? 'deal-bien' : 'deal-mal'}>
                                {dif >= 0
                                    ? `Bien jugado: te quedaste con el mejor por ${fmtM(dif)}.`
                                    : `Uh: dejaste pasar ${fmtM(-dif)}.`}
                            </p>
                        );
                    })()}
                    <button className="deal-si" onClick={cerrarRevelacion}>Sumarlo al equipo</button>
                </section>
            )}

            {ruleta?.tipo === 'premio' && (
                <RuletaPremio
                    estado={ruleta}
                    onGirar={girarRuleta}
                    onFrenar={alFrenarRuleta}
                    onApuesta={cobrarApuesta}
                    onCerrar={cerrarRuleta}
                />
            )}

            {porUbicar && (
                <section className="deal-ubicar">
                    <p>¿Dónde ponés a <strong>{porUbicar.jugador.nombre}</strong>?</p>
                    <div className="deal-botones">
                        {porUbicar.candidatos.map(c => (
                            <button key={c.i} className="deal-si" onClick={() => ubicar(porUbicar.jugador, c.i)}>{c.s}</button>
                        ))}
                    </div>
                </section>
            )}
            </div>

            <aside className="deal-derecha">
                <Cancha slots={slots} plantel={plantel} formacion={formacion} resaltar={ultimoPuesto} />
            </aside>
            </div>
        </div>
    );
}

function RevelaMaletin({ j, elegido = false, mio = false, etiqueta }) {
    return (
        <div className={`deal-revela${elegido ? ' ganador' : ' descartado'}${mio ? ' era-mio' : ''}`}>
            <span className="deal-revela-num">
                {j.numero ? `#${j.numero}` : 'la oferta'}{mio ? ' · el tuyo' : ''}
            </span>
            <div className="deal-revela-foto">
                <img src={getImgSrc(j.img) || defaultPlayerImg} alt={j.nombre}
                    onError={e => { e.target.src = defaultPlayerImg; }} />
            </div>
            <strong>{j.nombre}</strong>
            <span>{j.posiciones.join(', ')}</span>
            <em>{fmtM(j.valor)}</em>
            <span className="deal-revela-estado">{etiqueta || (elegido ? '✓ te lo quedaste' : 'lo dejaste')}</span>
        </div>
    );
}

/** Qué muestra cada maletín cerrado según el beneficio que salió en la ruleta inicial. */
function pistaDe(maletin, beneficio) {
    if (beneficio === 'pais') return maletin.pais || '—';
    if (beneficio === 'posiciones') return (maletin.posiciones || []).slice(0, 3).join(' ');
    // Puede no haber club: el jugador no tiene clubes cargados en la base.
    if (beneficio === 'club') return maletin.clubPista ? prettyClub(maletin.clubPista) : '?';
    return null;
}

const prettyClub = (k) => String(k).replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Ruleta inicial: se gira una vez y el beneficio dura toda la partida. */
function RuletaInicial({ onElegir }) {
    const [girando, setGirando] = useState(false);
    const [idx, setIdx] = useState(null);
    const [salio, setSalio] = useState(null);

    const girar = () => {
        setIdx(Math.floor(Math.random() * BENEFICIOS.length));
        setGirando(true);
    };

    return (
        <section className="deal-ruleta">
            <h2>Tu ventaja para toda la partida</h2>
            <p className="deal-ruleta-bajada">
                Los tres beneficios revelan algo de cada maletín <em>antes</em> de abrirlo.
            </p>

            <Rueda items={BENEFICIOS} ganadorIdx={idx} girando={girando}
                colorDe={(_, i) => ({ fondo: ['#1e3a5f', '#2e1065', '#14532d'][i % 3] })}
                onFin={() => { setGirando(false); setSalio(BENEFICIOS[idx]); }} />

            {!salio && !girando && <button className="deal-si deal-girar" onClick={girar}>Girar</button>}
            {girando && <p className="deal-ruleta-estado">Girando…</p>}
            {salio && (
                <div className="deal-ruleta-resultado">
                    <strong>
                        <svg viewBox="0 0 44 44" className="deal-icono-resultado" aria-hidden="true">
                            <Icono nombre={salio.icono} cx={22} cy={22} size={38} />
                        </svg>
                        {salio.titulo}
                    </strong>
                    <p>{salio.detalle}</p>
                    <button className="deal-si" onClick={() => onElegir(salio.id)}>Empezar</button>
                </div>
            )}
        </section>
    );
}

/** Ruleta de después de rechazar un trato. Girar es opcional. */
function RuletaPremio({ estado, onGirar, onFrenar, onCerrar, onApuesta }) {
    const { girando, resultado, detalle, pendiente, ganadorIdx } = estado;
    return (
        <div className="deal-modal-ruleta">
          <section className="deal-ruleta">
            <h2>{pendiente ? 'Rechazaste el trato' : (girando ? 'Girando…' : '¡Salió!')}</h2>
            {pendiente && <p className="deal-ruleta-bajada">Podés girar la ruleta. Hay premios y hay castigos.</p>}

            {!resultado && (
                <Rueda items={PREMIOS} ganadorIdx={ganadorIdx ?? null} girando={girando}
                    colorDe={(p) => ({ fondo: p.color === 'amarillo' ? '#78350f' : (p.bueno ? '#14532d' : '#7f1d1d') })}
                    onFin={onFrenar} />
            )}

            {pendiente && (
                <div className="deal-botones">
                    <button className="deal-si" onClick={onGirar}>Girar</button>
                    <button className="deal-no" onClick={onCerrar}>Paso</button>
                </div>
            )}

            {resultado && (
                <div className={`deal-ruleta-resultado ${resultado.bueno ? 'bueno' : 'malo'}`}>
                    <strong className={resultado.id === 'calavera' ? 'deal-calavera' : ''}>{resultado.titulo}</strong>
                    <p>{resultado.detalle}</p>
                    {/* Cada premio tiene su propia escena: el sobre se abre, la calavera parte la
                        carta, los cambios giran una carta sobre la otra. */}
                    <EfectoPremio premio={resultado} detalle={detalle} onListo={onCerrar} onApuesta={onApuesta} />
                    {detalle?.tipo === 'error' && <p className="deal-mal">No se pudo aplicar: {detalle.mensaje}</p>}
                </div>
            )}
          </section>
        </div>
    );
}

function Header({ navigate }) {
    return (
        <header className="deal-header">
            <button className="deal-volver" onClick={() => navigate('/')}>← Volver</button>
            <h1>Fichaje o No Fichaje</h1>
        </header>
    );
}

function Cancha({ slots, plantel, formacion, resaltar }) {
    const puestos = layoutFormacion(slots);
    return (
        <div className="deal-cancha-wrap">
            <div className="deal-cancha">
                <div className="deal-cancha-lineas" aria-hidden="true">
                    <span className="deal-medio" /><span className="deal-circulo" />
                    <span className="deal-area deal-area--abajo" /><span className="deal-area deal-area--arriba" />
                </div>
                {puestos.map(({ slot, indice, top, left }) => {
                    const j = plantel[indice];
                    return (
                        <div key={indice}
                            className={`deal-puesto${j ? ' lleno' : ''}${resaltar === indice ? ' recien' : ''}`}
                            style={{ top: `${top}%`, left: `${left}%` }}
                            title={j ? `${j.nombre} — ${fmtM(j.valor)}` : slot}>
                            <div className="deal-puesto-foto">
                                {j
                                    ? <img src={getImgSrc(j.img) || defaultPlayerImg} alt={j.nombre}
                                        onError={e => { e.target.src = defaultPlayerImg; }} />
                                    : <span className="deal-puesto-vacio">{slot}</span>}
                            </div>
                            <span className="deal-puesto-tag">{j ? j.nombre.split(' ').slice(-1)[0] : slot}</span>
                            {j && <span className="deal-puesto-valor">{fmtM(j.valor)}</span>}
                        </div>
                    );
                })}
            </div>
            <div className="deal-cancha-pie">
                <span>{formacion}</span>
                <span>{Object.keys(plantel).length}/11</span>
            </div>
            <div className="deal-total">
                <span>Valor del plantel</span>
                <strong>€{fuerzaEquipo(plantel)}M</strong>
            </div>
        </div>
    );
}


